import React, { useState, useEffect, useRef } from "react";
import { CalendarType } from "../../../common/Calendar/Calendar.constants";
import { COMPLIANCE_ACTIVITY_INDICATION, COMPLIANCE_ACTIVITY_ORDER, ComplianceActivityStatus, setUserDetailsInFilters } from "../../../../constants/Compliance.constants";
import dayjs from "dayjs";
import styles from "./ComplianceOwnerDashboard.module.css";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../../common/Table";
import { copyObject } from "../../../../utils/common";
import { hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";

type Props = {
    type: CalendarType,
    dateRange: { from: any, to: any },
    dataChanged?: any,
    filters?: any[],
    dates?: any[],
    stats: any
}
const colors: any = {};

export default function ComplianceOwnerDashboardActivities(props: Props) {
    const isEscalationManager = hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);
    const { type, dateRange, dates, dataChanged, filters } = props;
    const [data, setData] = useState<any>([]);
    const [__, setResponse] = useState<any>({})
    const [payload, setPayload] = useState<any>(null);
    const payloadRef = useRef<any>();
    payloadRef.current = payload;
    const [ _, setStats ] = useState<any[]>([]);
    const stats = props.stats

    function StatusTmpl({ activity }: any) {
        return (
            <div key={activity.status} className="mb-2">
                {
                    activity.status === ComplianceActivityStatus.DUE &&
                    <>
                        <div className="fw-bold">Pending Activities: {activity.count}</div>
                        <div>Finish before the due date</div>
                        <div className="text-md fw-bold fst-italic text-warn">Days Left: {activity.diff} {activity.diff < 2 ? 'day' : 'days'}</div>
                    </>
                }
                {
                    activity.status === ComplianceActivityStatus.NON_COMPLIANT &&
                    <>
                        <div className="fw-bold">Non-Compliant Activities: {activity.count}</div>
                        <div>These are on high priority and need immediate action.</div>
                        <div className="text-md fw-bold fst-italic text-error">Overdue for: {activity.diff} {activity.diff < 2 ? 'day' : 'days'}</div>
                    </>
                }
                {
                    activity.status === ComplianceActivityStatus.REJECTED &&
                    <>
                        <div className="fw-bold">Rejected Activities: {activity.count}</div>
                        <div>Review and re submit, if applicable.</div>
                    </>
                }
                {
                    activity.status === ComplianceActivityStatus.PENDING &&
                    <>
                        <div className="fw-bold">Submitted Activities: {activity.count}</div>
                    </>
                }
                {
                    activity.status === ComplianceActivityStatus.APPROVED &&
                    <>
                        <div className="fw-bold">Approved Activities: {activity.count}</div>
                    </>
                }
            </div>
        )
    }

    function hasFilters(field = 'start_date_gteq') {
        const _filters = (payloadRef.current || {}).filters || [];
        const column = _filters[field];
        return Boolean(column);
    }

    function updateData(groups: any) {
        if (dateRange && groups) {
            const { from, to } = dateRange;
            const _validGroups = groups.filter(({ date }: any) => {
                const [dd, mm, yyyy] = date.split('-');
                const dateObj = new Date(`${yyyy}-${mm}-${dd}`)
                return dateObj >= from && dateObj <= to;
            });
            const _data: { date: any; activities: any; }[] = [];
            _validGroups.map((group: any) => {
                const [dd, mm, yyyy] = group.date.split('-');
                return {
                    ...group,
                    date: `${yyyy}-${mm}-${dd}`
                }
            }).forEach((group: any) => {
                const activities = group.activities.sort((a: any, b: any) => {
                    const v1 = a.status;
                    const v2 = b.status;
                    return COMPLIANCE_ACTIVITY_ORDER.indexOf(v1) > COMPLIANCE_ACTIVITY_ORDER.indexOf(v2) ? 1 : -1
                });

                _data.push({
                    date: group.date,
                    activities: activities.map((x: any) => {
                        if (x.status === ComplianceActivityStatus.DUE) {
                            const currentDate = dayjs(new Date()).startOf('D').toDate();
                            const diff = dayjs(group.date).diff(currentDate, 'd');
                            x.diff = diff + 1;
                        } else if (x.status === ComplianceActivityStatus.NON_COMPLIANT) {
                            const currentDate = dayjs(new Date()).startOf('D').toDate();
                            const diff = dayjs(group.date).diff(currentDate, 'd');
                            x.diff = (diff * -1) + 1;
                        }
                        return x;
                    })
                });
            });
            setData(_data);
            if (dataChanged) {
                dataChanged({ dates, data: _data });
            }
        }
    }

    useEffect(() => {
        if (filters) {
            setPayload({ ...DEFAULT_OPTIONS_PAYLOAD, filters: setUserDetailsInFilters(copyObject(filters)), pagination: null, sort: null });
        }
    }, [filters]);

    function getTagColor() {
        var color = '#';
        for (var i = 0; i < 6; i++) {
            color += Math.floor(Math.random() * 10);
        }
        return color;
    }

    useEffect(() => {
        if (dateRange) {
            updateData(stats.compliance_group_by_date);
        }
    }, [dateRange]);

    useEffect(() => {
        if (stats) {
            const activityTypesStats = stats.counts_by_activity_types || {};
            setStats(Object.keys(activityTypesStats).map((key: string) => {
                if (!colors[key]) {
                    colors[key] = getTagColor();
                }
                const value = activityTypesStats[key]
                return { value: value || 0, label: key, style: { '--tag-color': colors[key] } as React.CSSProperties };
            }));
            updateData(stats.compliance_group_by_date)
            setResponse(stats.count_by_statusses)
        }
    }, [stats]);

    return (
        <>
            {
                (data || []).length === 0 &&
                <div className="card shadow align-items-center justify-content-center h-100">
                    <span className="fst-italic text-black-600">No data available</span>
                </div>
            }
            {
                (data || []).length > 0 &&
                <div className={`card shadow overflow-auto ${type === CalendarType.MONTH ? 'mt-2' : ''}`}>
                    <div className="d-flex flex-column">
                        {
                            data.map((_d: any) => {
                                return (
                                    <div className={styles.activityItem} key={_d.date}>
                                        <div className="d-flex flex-row h-100" style={{ backgroundColor: COMPLIANCE_ACTIVITY_INDICATION[_d.activities[0].status] as any }}>
                                            <div className={`d-flex flex-column align-items-center w-100 justify-content-center ${styles.activityItemLeft}`}>
                                                <span className="fw-500">{dayjs(_d.date).format('ddd')}</span>
                                                <span className="text-xl fw-bold">{dayjs(_d.date).format('D')}</span>
                                            </div>
                                        </div>
                                        <div>
                                            {
                                                _d.activities.map((activity: any) => {
                                                    return (<StatusTmpl activity={activity} key={activity.status} />)
                                                })
                                            }
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
            }
        </>
    )
}